import React, {useState, useEffect} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {motion, AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import MegaMenu from './MegaMenu';
import ContactSidebar from './ContactSidebar';

const {FiMenu, FiX, FiPhone, FiChevronDown, FiArrowRight, FiMail} = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setShowMegaMenu(false);
    setExpandedItem(null);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navItems = [
    {name: 'Startseite', path: '/'},
    {
      name: 'Leistungen',
      path: '/leistungen',
      hasMegaMenu: true,
      subItems: [
        {name: 'Rohbau', path: '/leistungen/rohbau'},
        {name: 'Klinkerarbeiten', path: '/leistungen/klinkerarbeiten'},
        {name: 'Putzarbeiten', path: '/leistungen/putzarbeiten'},
        {name: 'Betonarbeiten', path: '/leistungen/betonarbeiten'},
        {name: 'Maurerarbeiten', path: '/leistungen/maurerarbeiten'},
        {name: 'Pflasterverlegung', path: '/leistungen/pflasterverlegung'}
      ]
    },
    {name: 'Über uns', path: '/ueber-uns'},
    {name: 'Referenzen', path: '/referenzen'},
    {name: 'Kontakt', path: '/kontakt'}
  ];

  const toggleSubmenu = (itemName) => {
    setExpandedItem(expandedItem === itemName ? null : itemName);
  };

  return (
    <>
      {/* Increased z-index to z-[1000] to ensure it stays above embedded iframes/forms */}
      <header
        className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
        }`}
      >
        {/* Contact Bar - Neue Leiste über dem Haupt-Header - NUR auf Desktop sichtbar */}
        <div className="bg-gray-50 border-b border-gray-200 py-2 hidden lg:block">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-center text-sm">
              <div className="flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-6 text-gray-600">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiPhone} className="w-3 h-3 text-[#3466ad]" />
                  <span className="text-xs sm:text-sm">02452 860 70 19</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiMail} className="w-3 h-3 text-[#3466ad]" />
                  <span className="text-xs sm:text-sm">info@rudtbau.de</span>
                </div>
              </div>
              <div className="text-gray-500 text-xs sm:text-sm">
                Mo - Fr: 7:00 - 17:00 Uhr
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-4">
              <div className="w-16 h-16 flex items-center justify-center overflow-hidden">
                <img
                  src="https://canv.wasacon.com/rudt-bau/_assets/media/be98d041c9972c824096f49dc08c4e70.png"
                  alt="RUDT Bau Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">RUDT Bau</h1>
                <p className="text-xs sm:text-sm text-[#3466ad] font-medium whitespace-nowrap">
                  Rohbau · Klinker · Putz
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navItems.map((item) => (
                <div key={item.name} className="relative h-full flex items-center">
                  {item.hasMegaMenu ? (
                    <div
                      onMouseEnter={() => setShowMegaMenu(true)}
                      onMouseLeave={() => setShowMegaMenu(false)}
                      className="h-full flex items-center"
                    >
                      <Link
                        to={item.path}
                        className={`text-base font-medium transition-colors duration-200 flex items-center space-x-1 ${
                          location.pathname.startsWith(item.path)
                            ? 'text-[#3466ad]'
                            : 'text-gray-700 hover:text-[#3466ad]'
                        }`}
                      >
                        <span>{item.name}</span>
                        <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                      </Link>
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`text-base font-medium transition-colors duration-200 ${
                        location.pathname === item.path
                          ? 'text-[#3466ad]'
                          : 'text-gray-700 hover:text-[#3466ad]'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Desktop Contact Button */}
            <div className="hidden lg:flex items-center">
              <button
                onClick={() => setIsContactOpen(true)}
                className="bg-[#3466ad] text-white px-6 py-2.5 rounded-lg text-base font-medium hover:bg-[#2a5291] transition-colors duration-200"
              >
                Kontakt
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-3 text-gray-700 hover:text-[#3466ad] transition-colors relative z-50"
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mega Menu */}
        <AnimatePresence>
          {showMegaMenu && (
            <div
              onMouseEnter={() => setShowMegaMenu(true)}
              onMouseLeave={() => setShowMegaMenu(false)}
            >
              <MegaMenu />
            </div>
          )}
        </AnimatePresence>
      </header>

      {/* Mobile Fullscreen Menu - Fixed outside header */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 bg-white z-[9999] lg:hidden"
          >
            <motion.div
              initial={{x: '100%'}}
              animate={{x: 0}}
              exit={{x: '100%'}}
              transition={{type: 'spring', damping: 25, stiffness: 200}}
              className="h-full flex flex-col"
            >
              {/* Mobile Menu Header - Kompakter */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 flex items-center justify-center overflow-hidden">
                    <img
                      src="https://canv.wasacon.com/rudt-bau/_assets/media/be98d041c9972c824096f49dc08c4e70.png"
                      alt="RUDT Bau Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">RUDT Bau</h2>
                    <p className="text-xs text-[#3466ad] font-medium">Rohbau · Klinker · Putz</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 text-gray-700 hover:text-[#3466ad] transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-7 h-7" />
                </button>
              </div>

              {/* Mobile Menu Content - Optimierte Abstände */}
              <div className="flex-1 overflow-y-auto bg-white">
                <div className="px-4 py-4 space-y-1">
                  {navItems.map((item) => (
                    <div key={item.name} className="border-b border-gray-100 last:border-b-0">
                      {item.hasMegaMenu ? (
                        <div>
                          <button
                            onClick={() => toggleSubmenu(item.name)}
                            className="w-full flex items-center justify-between py-3 text-lg text-gray-700 hover:text-[#3466ad] font-medium transition-colors"
                          >
                            <span>{item.name}</span>
                            <SafeIcon
                              icon={FiChevronDown}
                              className={`w-5 h-5 transition-transform duration-200 ${
                                expandedItem === item.name ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                          <AnimatePresence>
                            {expandedItem === item.name && (
                              <motion.div
                                initial={{height: 0, opacity: 0}}
                                animate={{height: 'auto', opacity: 1}}
                                exit={{height: 0, opacity: 0}}
                                transition={{duration: 0.3, ease: 'easeInOut'}}
                                className="overflow-hidden"
                              >
                                <div className="pl-4 pb-3 space-y-1">
                                  {/* Neu: Link zur Übersichtsseite ganz oben im Menü */}
                                  <Link
                                    to={item.path}
                                    className="flex items-center justify-between py-3 px-4 text-base font-semibold text-[#3466ad] bg-blue-50/50 hover:bg-blue-50 rounded-lg transition-colors mb-1"
                                    onClick={() => setIsMenuOpen(false)}
                                  >
                                    <span>Alle Leistungen ansehen</span>
                                    <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                                  </Link>
                                  {item.subItems.map((subItem) => (
                                    <Link
                                      key={subItem.name}
                                      to={subItem.path}
                                      className="block py-3 px-4 text-base text-gray-600 hover:text-[#3466ad] hover:bg-gray-50 rounded-lg transition-colors"
                                      onClick={() => setIsMenuOpen(false)}
                                    >
                                      {subItem.name}
                                    </Link>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <Link
                          to={item.path}
                          className="block py-3 text-lg text-gray-700 hover:text-[#3466ad] font-medium transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile CTA Section - Noch kompakter */}
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <a
                      href="tel:+4924528607019"
                      className="flex items-center justify-center space-x-2 text-sm text-gray-700 bg-white px-3 py-2.5 rounded-lg border border-gray-200 hover:border-[#3466ad] hover:text-[#3466ad] transition-colors"
                    >
                      <SafeIcon icon={FiPhone} className="w-4 h-4" />
                      <span className="font-medium">Anrufen</span>
                    </a>
                    <a
                      href="mailto:info@rudtbau.de"
                      className="flex items-center justify-center space-x-2 text-sm text-gray-700 bg-white px-3 py-2.5 rounded-lg border border-gray-200 hover:border-[#3466ad] hover:text-[#3466ad] transition-colors"
                    >
                      <SafeIcon icon={FiMail} className="w-4 h-4" />
                      <span className="font-medium">E-Mail</span>
                    </a>
                  </div>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsContactOpen(true);
                    }}
                    className="block w-full bg-[#3466ad] text-white px-6 py-2.5 rounded-lg text-base font-semibold text-center hover:bg-[#2a5291] transition-colors shadow-lg"
                  >
                    Kontaktinformationen anzeigen
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contact Sidebar */}
      <ContactSidebar isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </>
  );
};

export default Header;